/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../lib/@waca/core-client/js/core-client/utils/PerfUtils',
	'../../lib/@waca/core-client/js/core-client/utils/Deferred',
	'underscore',
	'jquery',
	'../widgets/PropertiesUtil',
	'../widgets/error/ErrorView',
	'../EventChannelRouterHelper',
	'../layout/LayoutHelper'
], function(
	PerfUtils,
	Deferred,
	_,
	$,
	PropertiesUtil,
	ErrorView,
	ChannelRouterHelper,
	LayoutHelper
) {

	const ERRORCODE = {
		'LOAD': 20,
		'UNLOAD': 21,
		'WIDGET_ALREADY_LOADED': 22,
		'RENDER': 23,
		'UNLOADING_A_LOADING_WIDGET': 24
	};

	let _trackingId = 0;

	class WidgetLoader {
		constructor (params) {
			this.ERROR = ERRORCODE;

			/**
			 * Hash to keep track of the widgets that are in the process of being loaded
			 *
			 * @type {Object}
			 */
			this.loadingWidgets = {};
			this.failedWidgets = {};
			this.eventGroups = null;
			this.boardModel = null;
			this._initializeParams(params);

			//TODO use a namespace prefix/suffix for dom nodes for widgets ??

			this.registerEvents();
		}

		_initializeParams (params) {

			// Used to keep track of the widget API response promise (i.e. getWidget(id))
			this.widgetReadyPromises = {};
			// Used to tack request for the widget API before the widget is loaded
			this.pendingWidgetResolveRequests = {};

			this.services = params.services;
			this.appSettings = params.appSettings;

			this.contentFeatureLoader = params.contentFeatureLoader;

			this.dashboardApi = params.dashboardApi;
			this.canvas = params.canvas;
			this.eventRouter = params.eventRouter;

			this.logger = this.dashboardApi.getFeature('Logger');

			this.widgetRegistry = params.widgetRegistry;
			this.loadedWidgets = params.loadedWidgets;

			if (params.boardModel) {
				this.eventGroups = params.boardModel.eventGroups;
				this.boardModel = params.boardModel;
				this.topLayoutModel = params.boardModel.layout;
			}

			this.channelRouterHelper = new ChannelRouterHelper({
				eventRouter: this.eventRouter
			});
		}

		registerEvents () {
			this.eventGroups.on('change:id', this.onEventGroupIdChange, this);
			this.eventGroups.on('change:widgetIds', this.onWidgetIdsChange, this);
			if (this.boardModel) {
				this.boardModel.on('change:eventGroups', this.onEventGroupsChange, this);
			}
		}

		onEventGroupIdChange (event) {
			var options = {
				sender: event.sender,
				payloadData: event.data
			};
			var eventRouter = this.channelRouterHelper.getChannelRouter(event.model.id);
			_.each(event.model.widgetIds, function(widgetId) {
				if (this.loadedWidgets[widgetId]) {
					this.loadedWidgets[widgetId].setEventRouter(eventRouter, options);
				}
			}.bind(this));
		}

		onWidgetIdsChange (event) {
			var options = {
				sender: event.sender,
				payloadData: event.data
			};
			// process the widgets that are added or removed first
			var changedWidgets, newEventRouter;
			if (event.prevValue.length > event.value.length) {
				// widgets are removed from group
				changedWidgets = _.difference(event.prevValue, event.value);
				newEventRouter = null;
			} else {
				// widgets are added to group
				changedWidgets = _.difference(event.value, event.prevValue);
				newEventRouter = this.channelRouterHelper.getChannelRouter(event.model.id);
			}
			_.each(changedWidgets, function(widgetId) {
				if (this.loadedWidgets[widgetId]) {
					this.loadedWidgets[widgetId].setEventRouter(newEventRouter, options);
				}
			}.bind(this));

			// process the existing widgets
			const remainingWidgets = this.dashboardApi.getFeature('EventGroups').getContentIdList(event.model.id);
			let eventRouter = this.channelRouterHelper.getChannelRouter(event.model.id);

			_.each(remainingWidgets, function(widgetId) {
				//check here to see if the widget(s) in the event groups are not deleted
				if (this.loadedWidgets[widgetId] && this.canvas.getContent(widgetId)) {
					//TODO: not sure why we have to set event router here. Need to revisit this.
					this.loadedWidgets[widgetId].setEventRouter(eventRouter, options);
				}
			}.bind(this));
		}

		onEventGroupsChange (event = {}) {
			if (event.name === 'add') {
				const value = event.value || {};
				if (value.widgetIds instanceof Array) {
					const eventRouter = this.channelRouterHelper.getChannelRouter(event.value.id);
					value.widgetIds.forEach(id => {
						if (this.loadedWidgets[id]) {
							this.loadedWidgets[id].setEventRouter(eventRouter, { silent: true });
						}
					});
				}
			}
		}

		registerWidgetToEventGroup (widgetId, transactionInfo) {
			var widget = this.getWidget(widgetId);
			if (widget) {
				const transactionId = transactionInfo && transactionInfo.undoRedoTransactionId;
				widget.registerEventGroup(transactionId, { transactionInfo: transactionInfo });
			}
		}

		unregisterWidgetFromEventGroup (widgetId, transactionId, options) {
			this.eventGroups.removeWidgetsFromGroup([widgetId], {
				payloadData: {
					undoRedoTransactionId: transactionId,
					skipUndoRedo: options.data && options.data.skipUndoRedo
				},
				sender: options.sender
			});
		}

		/**
		 *
		 * @param {string} widgetId - id of the widget to load
		 * @param {object} widgetSpec - spec of the widget to load
		 * @param {object} domNode - DOM object where to load the widget
		 * @param {function} cbOnLoad - callback on load
		 * @param {function} cbOnError - callback on error
		 * @param {string} transactionId - transaction id
		 * @param {object} addPayload - payload information for the add/load operation
		 * @returns {promise}  resolved if successful, rejected when fails
		 */
		loadWidget (widgetId, widgetSpec, domNode, cbOnLoad, cbOnError, transactionId, addPayload) {
			var trackingId = _trackingId;
			_trackingId++;
			let transactionInfo = {};
			transactionInfo.undoRedoTransactionId = transactionId;
			transactionInfo.skipUndoRedo = addPayload ? addPayload.skipUndoRedo === true : false;

			this.logger.debug('Widget load start (' + trackingId + ')', widgetId, widgetSpec, domNode);

			return this._invokeLifeCycleHandlers('pre:widget.load', {
				modelId: widgetId,
				widgetSpec: widgetSpec
			}).then(function(){
				var result;

				if (!this.loadedWidgets[widgetId] && this.loadingWidgets[widgetId]) {
					//If loadWidget is called while already loading a widget,
					//save callbacks for execution when the widget is loaded (and exit).
					this.loadingWidgets[widgetId].cbOnLoad.push(cbOnLoad);
					this.loadingWidgets[widgetId].cbOnError.push(cbOnError);
				} else if (widgetId && widgetSpec) {
					var widget = this.loadedWidgets[widgetId];
					if (!widget && !this.loadingWidgets[widgetId]) {
						try {
							result = this._loadWidget(widgetId, widgetSpec, domNode, cbOnLoad, cbOnError, trackingId, transactionInfo);
						} catch (e) {
							this._onError(cbOnError, ERRORCODE.LOAD, widgetId, e, trackingId);
							result = Promise.reject(e);
						}
					} else {
						this._onError(cbOnError, ERRORCODE.WIDGET_ALREADY_LOADED, widgetId, null, trackingId);
						result = Promise.reject(new Error('Widget already loaded'));
					}
				}

				return result;
			}.bind(this))
				.catch(this.logger.error.bind(this.logger));
		}

		unLoadWidget (widgetId, cbOnError, payload = {}) {
			if (widgetId) {
				const transactionId = payload.data ? payload.data.undoRedoTransactionId : undefined;
				this.logger.debug('Unloading widget', widgetId);

				if (this.loadingWidgets[widgetId]) {
					// Shouldn't unload a widget until it's loaded fully
					this._onError(cbOnError, ERRORCODE.UNLOADING_A_LOADING_WIDGET, widgetId);
					return;
				}
				var widget = this.loadedWidgets[widgetId];

				this.unregisterWidgetFromEventGroup(widgetId, transactionId, payload);

				if (widget && widget.destroy) {
					try {
						widget.destroy(transactionId);
					} catch (e) {
						this._onError(cbOnError, ERRORCODE.UNLOAD, widgetId, e);
					} finally {
						this.loadedWidgets[widgetId] = null;
						this.widgetReadyPromises[widgetId] = null;
					}
				}

				var el = document.getElementById(widgetId);
				if (el && el.getElementsByClassName('widgetContentWrapper').length) {
					el.getElementsByClassName('widgetContentWrapper')[0].innerHTML = '';
				}
			}
		}

		/**
		 * Set the state of a widget to 'loading' by adding an entry into the loadingWidgets map by widgetId.
		 * This entry is also responsible for ensuring that, when multiple calls to loadWidget on the same id
		 * occur, callbacks are not lost...they will be executed postLoad.
		 * @param widgetId  The id of the widget to set into loading state.
		 */
		_setLoading (widgetId) {
			this.loadingWidgets[widgetId] = {
				cbOnLoad: [],
				cbOnError: []
			};
		}

		_loadWidget (widgetId, widgetSpec, domNode, cbOnLoad, cbOnError, trackingId, transactionInfo) {

			cbOnLoad = cbOnLoad || function() {};
			if (!this.loadingWidgets[widgetId]) {
				this._setLoading(widgetId);
			}

			var widgetModule = this.widgetRegistry[widgetSpec.type] && this.widgetRegistry[widgetSpec.type].widget;
			if (!widgetModule) {
				try {
					cbOnLoad({
						'id': widgetId,
						'widget': null
					});
				} catch(error) {
					cbOnError(error);
				}

				return;
			}

			var dfd = new Deferred();
			var onLoadWidget = _.partial(this._onLoadWidget.bind(this), widgetId, widgetSpec, widgetModule, domNode, cbOnLoad, cbOnError, trackingId, transactionInfo);

			require([
				widgetModule
			], function(Widget) {
				return onLoadWidget(Widget)
					.then(dfd.resolve.bind(dfd), dfd.reject.bind(dfd));
			}, function(e) {
				// If the require fails, errback should be called
				// TODO: This doesn't get called by DOJO, but it will once we switch to require.js
				dfd.reject(e);
				delete this.loadingWidgets[widgetId];
				this._onError(cbOnError, ERRORCODE.LOAD, widgetId, e, trackingId);
			}.bind(this));

			return dfd.promise;
		}

		_onLoadWidget (widgetId, widgetSpec, widgetModule, domNode, cbOnLoad, cbOnError, trackingId, transactionInfo, Widget) {
			let nParent;
			let nWidgetContainer;
			let nParentWidgetContainer;
			let result;

			try {
				if (!this.loadingWidgets[widgetId]) {
					// This widget isn't in a loading state, we shouldn't continue loading it.
					// This can happen if the WidgetLoader is destroyed and there are pending loads
					return Promise.reject(new Error(`Widget '${widgetId}' is not in a loading state. Unable to load.`));
				}

				PerfUtils.createPerformanceMark({
					'component': 'dashboard',
					'name': 'loadWidget',
					'state': 'start'
				});

				this.logger.debug('Widget load finish (' + trackingId + ')');

				result = this._invokeLifeCycleHandlers('post:widget.load', {
					modelId: widgetId,
					widgetSpec: widgetSpec
				}).then(() => {
					return this.contentFeatureLoader.whenContentReady(widgetId);
				}).then(function(){
					// @todo investigate
					nParentWidgetContainer = nParent = document.getElementById(widgetId) || domNode;
					if (nParent && nParent.getElementsByClassName('widgetContentWrapper').length) {
						nParent = nParent.getElementsByClassName('widgetContentWrapper')[0];
					}

					if(!nParent) {
						this.logger.error('Could not find layout node for widget with id: "' +widgetId+ '"');
						return;
					} else {
						var createContentNode = true;
						var widgetRegistry = this.widgetRegistry[widgetSpec.type];
						if (widgetRegistry) {
							createContentNode = this.widgetRegistry[widgetSpec.type].createContentNode;
						}

						if (createContentNode) {
							nWidgetContainer = document.createElement('div');

							// Apply widget type specific css
							nWidgetContainer.className = widgetSpec.type + 'WidgetContent widgetContent';

							nWidgetContainer.id = widgetId + '_';
							$(nParent).append(nWidgetContainer);
						} else {
							nWidgetContainer = nParent;
						}

						//TODO use namespace for widget dom node?
						var options = {
							// TODO - to be removed after refactoring
							services: this.services,
							appSettings: this.appSettings,
							// TODO END - to be removed after refactoring

							dashboardApi: this.dashboardApi,
							id: widgetId,
							canvas: this.canvas,
							content: this.canvas && this.canvas.getContent(widgetId),
							initialConfigJSON: widgetSpec,
							el: nWidgetContainer,
							widgetContainer: nParentWidgetContainer,
							registry: widgetRegistry,
							eventRouter: this.eventRouter,
							errorView: new ErrorView(),
							propertiesUtil: PropertiesUtil,
							contentFeatureLoader: this.contentFeatureLoader
						};
						options = this._addScopedEventInfo(options, widgetSpec, transactionInfo);
						options = this._addConfigInfo(options);

						//The Endor LiveWidget expects the widget model in its constructor rather than waiting for onContainerReady.
						var widgetModelInstance = (this.boardModel && this.boardModel.widgetInstances && this.boardModel.widgetInstances[widgetId]);
						if (widgetModelInstance) {
							options.widgetModel = widgetModelInstance;
						} else {
							options.widgetSpec = widgetSpec;
						}

						return this._createWidget(options, cbOnLoad, cbOnError, Widget, trackingId, transactionInfo)
							.then(() => {
								PerfUtils.createPerformanceMark({
									'component': 'dashboard',
									'name': 'loadWidget',
									'state': 'end'
								});
							}, (error)=>{
								//prevent widgets which failed to load to be added to an event group
								this.failedWidgets[widgetId].setEventRouter(null);
								throw error;
							});
					}
				}.bind(this))
					.catch(this.logger.error.bind(this.logger));
			} catch (e) {
				// Make sure the widget is no longer in loading state even if we get an error
				delete this.loadingWidgets[widgetId];
				if (nParent) {
					nParent.removeChild(nWidgetContainer);
				}
				this._onError(cbOnError, ERRORCODE.LOAD, widgetId, e, trackingId);
				result = Promise.reject(e);
			}
			return result;
		}

		_createWidget (options, cbOnLoad, cbOnError, Widget, trackingId, transactionInfo){
			this.logger.debug('Widget create start (' + trackingId + ')');
			var widgetId = options.id;
			var widget = new Widget(options);

			let result;
			const renderWidget = () => {
				this.loadedWidgets[widgetId] = widget;
				return this._renderWidget(widget, widgetId, cbOnLoad, cbOnError, trackingId, transactionInfo);
			};

			if (widget.initialize){
				result = widget.initialize().then(() => {
					return renderWidget();
				}, (error) =>{
					//widget failed to load and is added to failedWidgets object, so that it does not get assigned an event group
					this.failedWidgets[widgetId] = widget;
					if (error && error.uiErrorMessage && widget.showError) {
						widget.showError(error.uiErrorMessage);
					}
					throw error;
				});
			} else {
				result = renderWidget();
			}

			return result;
		}

		_renderWidget (widget, widgetId, cbOnLoad, cbOnError, trackingId, transactionInfo) {

			this.logger.debug('Widget render start (' + trackingId + ')');
			const timeMarker  = 'widget ' + widgetId + ' render';
			PerfUtils.createPerformanceMark({
				'component': 'dashboard',
				'name': timeMarker,
				'state': 'start'
			});

			let result;
			// If we have a pending request for the widget, we resolve now.
			if (this.pendingWidgetResolveRequests[widgetId]){
				this.pendingWidgetResolveRequests[widgetId](widget);
				delete this.pendingWidgetResolveRequests[widgetId];
			}

			//If any callbacks were processed during load, we need to call them now....
			var cbOnLoadCallbacks = this.loadingWidgets[widgetId].cbOnLoad;
			var cbOnErrorCallbacks = this.loadingWidgets[widgetId].cbOnError;
			delete this.loadingWidgets[widgetId];

			try {
				if (!widget.content || widget.content.getType() !== 'widget.filter') {
					this.registerWidgetToEventGroup(widgetId, transactionInfo);
				}

				if (widget.render) {
					if (widget.createVisualizationCompleteDeferred) {
						result = widget.createVisualizationCompleteDeferred
							.then(() => {
								return Promise.resolve() // protection against $ deferred. TODO: please remove after all widgets return a proper promise
									.then(widget.render.bind(widget))
									.then( () => {
										PerfUtils.createPerformanceMark({
											'component': 'dashboard',
											'name': timeMarker,
											'state': 'end'
										});

									})
									.catch((error) => {
										// render may reject when parent is not visible. ignore, but log the error.
										this.logger.error('[WidgetLoader] widget render error', error);
									});
							});
					} else {
						result = Promise.resolve(widget.render())
							.then( () => {
								PerfUtils.createPerformanceMark({
									'component': 'dashboard',
									'name': timeMarker,
									'state': 'end'
								});

							})
							.catch((error) => {
								// render may reject when parent is not visible. ignore but log the error.
								this.logger.error('[WidgetLoader] widget render error', error);

								_.each(cbOnErrorCallbacks, (cb) => {
									this._onError(cb, ERRORCODE.RENDER, widgetId, error, trackingId);
								});
								this._onError(cbOnError, ERRORCODE.RENDER, widgetId, error, trackingId);
								throw error;
							});
					}
				} else {
					result = Promise.resolve();
				}

				_.each(cbOnLoadCallbacks, function(cb) {
					cb({
						'id': widgetId,
						'widget': widget
					});
				});

				cbOnLoad({
					'id': widgetId,
					'widget': widget
				});

				this.logger.debug('Widget render finish (' + trackingId + ')');

			} catch (e) {
				_.each(cbOnErrorCallbacks, (cb) => {
					this._onError(cb, ERRORCODE.RENDER, widgetId, e, trackingId);
				});
				this._onError(cbOnError, ERRORCODE.RENDER, widgetId, e, trackingId);
				result = Promise.reject(new Error(''));
			}
			return result;
		}

		_getChannelId (containerPageId, widgetId, transactionInfo) {
			var eventGroup;
			if (this.eventGroups) {
				eventGroup = this.eventGroups.findGroup(widgetId);
				if (!eventGroup) {
					eventGroup = this.eventGroups.getDefaultGroup(containerPageId, {
						payloadData: transactionInfo
					});
				}
			}
			// use the pageId if eventGroups are not defined
			return eventGroup ? eventGroup.id : containerPageId;
		}

		/**
		 * If channelRouterHelper is available, add containerPageId and update eventRouter
		 *
		 * @returns updated options
		 */
		_addScopedEventInfo (options, widgetSpec, transactionInfo) {
			if (this.channelRouterHelper) {
				// Custom widget may declare filter support from it's definition
				var filterSupport = this.widgetRegistry[widgetSpec.type].filter;

				// Loading board case, containerPageId is passed in widgetSpec.
				// If not, it is onDrop case. Use current container page id.
				options.containerPageId = LayoutHelper.getContainerPageId(this.topLayoutModel, widgetSpec.id);

				var supported = [
					'data',
					'live'
				];

				if (supported.indexOf(widgetSpec.type) !== -1 || filterSupport) {
					var channelId = this._getChannelId(options.containerPageId, widgetSpec.id, transactionInfo);
					options.eventRouter = this.channelRouterHelper.getChannelRouter(channelId);
				}
			}

			return options;
		}

		/**
		 * Updates the options for the widget with any app/view level constraints
		 * @param {object} options
		 */
		_addConfigInfo (options = {}) {
			options.focusModeDisabled = !!this.dashboardApi.getConfiguration('focusModeDisabled');
			options.interactivitySettings = this.dashboardApi.getConfiguration('interactions');
			return options;
		}

		cleanup () {
			var map = this.loadedWidgets;
			for (var widgetId in map) {
				if (map.hasOwnProperty(widgetId)) {
					var widget = map[widgetId];
					if (widget && widget.destroy) {
						widget.destroy();
						this.loadedWidgets[widgetId] = null;
						this.widgetReadyPromises[widgetId] = null;
					}
				}
			}
			this.loadingWidgets = {};

			if (this.eventGroups) {
				this.eventGroups.off('change:id', this.onEventGroupIdChange, this);
				this.eventGroups.off('change:widgetIds', this.onWidgetIdsChange, this);
			}

			if (this.boardModel) {
				this.boardModel.off('change:eventGroups', this.onEventGroupsChange, this);
			}

			if (this.channelRouterHelper) {
				this.channelRouterHelper.destroy();
				delete this.topLayoutModel;
			}

		}

		isLoaded (id) {
			return id ? (this.loadedWidgets[id] !== null && typeof this.loadedWidgets[id] !== 'undefined') : false;
		}

		isLoading (id) {
			return this.loadingWidgets[id];
		}

		getWidget (id) {
			return id ? this.loadedWidgets[id] : undefined;
		}

		getWidgetAsync (id){
			if (!this.widgetReadyPromises[id]) {
				this.widgetReadyPromises[id] = new Promise((resolve) => {
					let widget;
					if (this.isLoaded(id)){
						widget = this.getWidget(id);
					}
					if (!widget){
						this.pendingWidgetResolveRequests[id] = resolve;
					}else {
						resolve(widget);
					}
				});
			}
			return this.widgetReadyPromises[id];
		}

		/**
		 * Call the named API on all loaded widgets that implement it.
		 * Optionally, return the result of those calls as an array.
		 * @returns An array of results (of arbitrary form) that the caller can examine.
		 *			If the api does not return anything, this array will be empty.
		 */
		runAPIOnAllWidgets (api, pageId) {
			var resultSet = [];
			_.each(_.keys(this.loadedWidgets), function(widgetKey) {
				var loadedWidget = this.loadedWidgets[widgetKey];
				if (loadedWidget && loadedWidget[api] && (!pageId || (loadedWidget.getContainerPageId() === pageId))) {
					var result = loadedWidget[api]();
					if (result) {
						resultSet.push(result);
					}
				}
			}.bind(this));
			return resultSet;
		}

		_onError (fOnError, errCode, oWidgetInfo, e, trackingId) {
			var error = {
				'errorCode': errCode,
				'widget': oWidgetInfo,
				'exception': e
			};
			if (fOnError) {
				setTimeout(function() {
					fOnError(error);
				}, 1);
			}

			this.logger.error('Error loading widget', trackingId, error);
		}

		_invokeLifeCycleHandlers (name, payload) {
			return this.dashboardApi.getFeature('.LifeCycleManager').invokeLifeCycleHandlers(name, payload);
		}

	}
	return WidgetLoader;
});
